import React from 'react';
import { useTranslation } from 'react-i18next';
import { Product, ProductType, getProductImageUrl } from '../../models/product/product';
import { Book } from '../../models/product/book';import { currencyService } from '../../services/currency-service';
interface ProductListItemProps {
  product: Product;
  onViewDetails: () => void;
  onAddToCart: () => void;
  isRTL?: boolean;
  isAddingToCart?: boolean;
}

const ProductListItem: React.FC<ProductListItemProps> = ({
  product,
  onViewDetails,
  onAddToCart,
  isRTL = false,
  isAddingToCart = false
}) => {
  const { t } = useTranslation();

  // Type-specific rendering helpers
  const getProductTypeIcon = () => {
    switch (product.type) {
      case ProductType.BOOK:
        return (
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        );
      case ProductType.AUDIO:
        return (
          <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          </svg>
        );
      case ProductType.VIDEO:
        return (
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case ProductType.MUSIC:
        return (
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
        );
      case ProductType.ART:
        return (
          <svg className="w-6 h-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        );
      case ProductType.GIFTS:
        return (
          <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
          </svg>
        );
      case ProductType.DEVOTIONALS:
        return (
          <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getRatingStars = (rating: number) => {
    switch (product.type) {
      case ProductType.BOOK:
        const book = product as Book;
        return (
          <div className={`flex flex-wrap items-center gap-3 text-sm text-gray-600 ${isRTL ? 'space-x-reverse' : ''}`}>
            {book.author && (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {book.author}
              </span>
            )}
            {book.pages && (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {book.pages} {t('products.books.pages', 'pages')}
              </span>
            )}
            {book.bookFormat && book.bookFormat.length > 0 && (
              <div className={`flex items-center gap-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                {book.bookFormat && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                    {book.bookFormat.toUpperCase()}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      
      case ProductType.AUDIO:
      case ProductType.VIDEO:
      case ProductType.MUSIC:
      default:
        return (
          <div className="text-sm text-gray-600">
            {product.merchant && (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {product.merchant.firstName} {product.merchant.lastName}
              </span>
            )}
          </div>
        );
    }
  };


  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 group">
      <div className={`flex gap-4 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Product Image */}
        <div className="flex-shrink-0 w-20 h-28 sm:w-24 sm:h-32 relative overflow-hidden rounded-lg bg-gray-100">
          {getProductImageUrl(product) ? (
            <img
              src={getProductImageUrl(product)}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/api/placeholder/150/200';
              }}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              {getProductTypeIcon()}
            </div>
          )}
          
          {/* Product Type Badge */}
          <div className={`absolute top-1 ${isRTL ? 'left-1' : 'right-1'}`}>
            <span className="px-2 py-1 bg-black bg-opacity-75 text-white text-xs rounded font-medium">
              {t(`products.categories.${product.type.toLowerCase()}`, product.type)}
            </span>
          </div>
        </div>

        {/* Product Details */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex-1">
              <div className={`flex justify-between items-start gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 group-hover:text-black transition-colors cursor-pointer" 
                    onClick={onViewDetails}>
                  {product.name}
                </h3>
                
                {/* Price */}
                <div className={`flex flex-col items-end ${isRTL ? 'items-start' : 'items-end'}`}>
                  <span className="text-lg font-bold text-green-600">
                    {currencyService.formatPrice(product.price)}
                  </span>
                  {product.originalPrice && product.originalPrice > product.price && (
                    <span className="text-sm text-gray-500 line-through">
                      {currencyService.formatPrice(product.originalPrice)}
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              {product.description && (
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                  {product.description}
                </p>
              )}

              {/* Product-specific Information */}
              <div className="mb-3">
                {/*{getProductSpecificInfo()}*/}
              </div>

              {/* Rating and Stock Status */}
              <div className={`flex items-center justify-between gap-4 mb-3 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                {/* Rating and stock status sections temporarily disabled */}
              </div>
            </div>

            {/* Action Buttons */}
            <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
              <button
                onClick={onViewDetails}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium touch-manipulation min-h-[44px] flex items-center justify-center"
              >
                {t('general.actions.view-details', 'View Details')}
              </button>
              
              <button
                onClick={onAddToCart}
                disabled={!product.inStock || isAddingToCart}
                className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium touch-manipulation min-h-[44px] flex items-center justify-center gap-2"
              >
                {isAddingToCart ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('general.actions.adding', 'Adding...')}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m1.6 8L5 3H3m4 10v6a1 1 0 001 1h10a1 1 0 001-1v-6M9 19a1 1 0 102 0 1 1 0 00-2 0zm8 0a1 1 0 102 0 1 1 0 00-2 0z" />
                    </svg>
                    {t('cart.actions.add-to-cart', 'Add to Cart')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Backward compatibility wrapper for BookListItem
interface BookListItemProps {
  book: Product;
  onViewDetails: () => void;
  onAddToCart: () => void;
  isRTL?: boolean;
  isAddingToCart?: boolean;
}

export const BookListItem: React.FC<BookListItemProps> = ({
  book,
  onViewDetails,
  onAddToCart,
  isRTL,
  isAddingToCart
}) => (
  <ProductListItem
    product={book}
    onViewDetails={onViewDetails}
    onAddToCart={onAddToCart}
    isRTL={isRTL}
    isAddingToCart={isAddingToCart}
  />
);

export default ProductListItem;